import "../App.css";
import { authFetch } from "./authService";
import { Battle } from "../models/Battle";
import { BattlePreview } from "../models/BattlePreview";

export async function getCharacterBattleResult(char1, char2) {
  const response = await authFetch("/api/battle", {
    method: "POST",
    body: JSON.stringify({ character1: char1, character2: char2 }),
  });
  const data = await response.json();
  if (!response.ok) {
    const err = new Error(data?.error || "Failed to get battle result.");
    err.errorCode = data?.errorCode;
    err.policy = data?.policy ?? null;
    err.accountStanding = data?.accountStanding ?? null;
    err.requiredPolicies = data?.requiredPolicies ?? null;
    err.enforcementPresentation = data?.enforcementPresentation ?? null;
    throw err;
  }
  return Battle.fromJSON(data);
}

export async function getBattleById(id) {
  const response = await authFetch(`/api/battle/${encodeURIComponent(id)}`);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || "Battle not found.");
  }
  return Battle.fromJSON(data);
}

export async function getTrending({ offset = 0, limit = 10, signal } = {}) {
  const response = await fetch(`/api/battle/trending/top?limit=${limit}&offset=${offset}`, { signal });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  return { items: BattlePreview.fromList(data.items), hasMore: data.hasMore };
}

export async function streamCharacterImage({ prompt, imageKey, size = "1024x1024", signal, onPartial, onDone, onError, endpoint = "/api/battle-image/stream" } = {}) {
  let res;
  try {
    res = await authFetch(endpoint, {
      method: "POST",
      body: JSON.stringify({ prompt, imageKey, size }),
      signal,
    });
  } catch (e) {
    if (e?.name !== "AbortError") onError?.({ errorCode: "generation_failed" });
    return;
  }

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    onError?.({
      errorCode: data?.errorCode || "generation_failed",
      policy: data?.policy ?? null,
      requiredPolicies: data?.requiredPolicies ?? null,
      presentationKey: data?.presentationKey ?? null,
      previewEligible: Boolean(data?.previewEligible),
      categoryDetails: Array.isArray(data?.categoryDetails) ? data.categoryDetails : [],
    });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        try {
          const event = JSON.parse(line.slice(6));
          if (event.type === "partial") onPartial?.(event);
          else if (event.type === "done") onDone?.(event);
          else if (event.type === "error") onError?.(event);
        } catch {}
      }
    }
  } catch (e) {
    if (e?.name !== "AbortError") onError?.({ errorCode: "generation_failed" });
  }
}

export async function getCharacterImage({ prompt, imageKey, size = "1024x1024", signal } = {}) {
  try {
    const res = await authFetch("/api/battle-image", {
      method: "POST",
      body: JSON.stringify({ prompt, imageKey, size, safeOriginal: true }),
      signal,
    });
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data?.errorCode || "generation_failed");
      err.errorCode = data?.errorCode || "generation_failed";
      err.policy = data?.policy ?? null;
      err.requiredPolicies = data?.requiredPolicies ?? null;
      err.presentationKey = data?.presentationKey ?? null;
      err.previewEligible = Boolean(data?.previewEligible);
      err.categoryDetails = Array.isArray(data?.categoryDetails) ? data.categoryDetails : [];
      throw err;
    }
    return data;
  } catch (error) {
    console.error("OpenAI Image API call failed:", error?.message);
    throw error;
  }
}
